import { useEffect, useRef, useState } from "react";
import { Settings2 } from "lucide-react";
import { Button, Checkbox } from "@ir/ui";
import type { Column } from "@ir/ui";

interface ColumnVisibilityButtonProps<T> {
  columns: Column<T>[];
  visibleColumnIds: string[];
  minimumReached: boolean;
  onToggle: (id: string) => void;
  onReset: () => void;
}

export function ColumnVisibilityButton<T>({
  columns,
  visibleColumnIds,
  minimumReached,
  onToggle,
  onReset,
}: ColumnVisibilityButtonProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="secondary"
        icon={Settings2}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
      >
        Columns
      </Button>
      {open && (
      <div
        role="dialog"
        aria-label="Choose table columns"
        className="absolute right-0 z-40 mt-2 w-72 rounded-xl border border-line bg-surface p-3 shadow-ir-lg"
      >
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-content">Show columns</p>
          <button
            type="button"
            onClick={onReset}
            className="text-xs font-medium text-gold-700 hover:text-gold-900 dark:text-gold-400"
          >
            Reset
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto pr-1">
          {columns.map((column) => {
            const label =
              column.mobileLabel ??
              (typeof column.header === "string" ? column.header : column.id);
            const checked = visibleColumnIds.includes(column.id);
            return (
              <Checkbox
                key={column.id}
                label={label || column.id}
                checked={checked}
                disabled={checked && minimumReached}
                onChange={() => onToggle(column.id)}
              />
            );
          })}
        </div>
        <p className="mt-2 text-xs text-content-subtle">
          At least one data column must remain visible. Actions stay available.
        </p>
      </div>
      )}
    </div>
  );
}
